import { queryKeyFn } from '@sit/client-shared';
import { queryClient } from '@web/hooks/api/query/client';
import { all, put } from 'redux-saga/effects';

import { reportError } from '../../helpers/newrelic';
import { calculateDefaultRowValues } from '../../helpers/rows';
import * as AutofillActions from '../Autofill/actions';
import {
  findDepartmentDimension,
  findLocationDimension,
  selectClientsData,
  selectItemsData,
  selectProjectsPerClientId,
  selectTasksPerProjectId,
  selectTimesheetData,
  selectTimesheetDimensions,
  selectUserData,
} from '../selectors';

function* HandleAutofillValues({ autofillValues, from, rowId }) {
  yield all(autofillValues.map((a) => put(AutofillActions.setAutofill({ rowId, a, from }))));
}

function* GenerateRowAutofill({ row, store }) {
  try {
    const timesheetData = selectTimesheetDimensions(store);
    const timesheet = selectTimesheetData(store);

    let projectId = 'all';

    if (timesheetData.showProject) {
      projectId =
        row.entries != null && row.entries.length > 0 && row.entries[0]?.confirmedProjectId ? row.entries[0].confirmedProjectId : undefined;
    }
    const tasksData = selectTasksPerProjectId(store, projectId);

    /** @type {[import('@tanstack/react-query').QueryKey, import('@sit/client-shared').GetApiDimensionsResponse | undefined][]} */
    const dimensionsQueriesData = queryClient.getQueriesData(
      queryKeyFn({
        path: '/dimensions',
        operationId: 'getApiDimensions',
        variables: {
          activeOnly: 'true',
          timesheetId: timesheet.id,
        },
      }),
    );

    let dimensionsData = dimensionsQueriesData?.[0]?.[1];
    // We want to find the queryKey that includes `projectId` if it exists.
    // If it doesn't exist, we want to use the first queryKey that includes `timesheetId`.
    if (dimensionsQueriesData && projectId)
      for (const [queryKey, queryData] of dimensionsQueriesData) {
        if (queryKey.length === 2 && queryKey[1].projectId === projectId) {
          dimensionsData = queryData;
          break;
        }
      }

    const departmentOptions = findDepartmentDimension(dimensionsData)?.values || [];
    const locationOptions = findLocationDimension(dimensionsData)?.values || [];
    const userData = selectUserData(store);
    let userAutofillValues = [];
    if (!timesheet.user_id || userData?.id === timesheet.user_id) {
      userAutofillValues = userData?.autofillValues;
    } else {
      // this is a staff timesheet, need to get the autofill values from the timesheet user
      // const employeesData = selectEmployeesData(store);
      // const timesheetUser = employeesData.find((e) => e.id === timesheet.user_id);
      // userAutofillValues = timesheetUser?.autofillValues;
    }
    const { pClientId, pDepartment, pDepartmentObj, pItemId, pLocation, pLocationObj, pProjectId, pTaskId, taskObj } =
      calculateDefaultRowValues({ row, timesheetDimensions: timesheetData, tasksData, departmentOptions, locationOptions });
    if (userAutofillValues && userAutofillValues.length > 0) {
      yield HandleAutofillValues({ autofillValues: userAutofillValues, rowId: row.id, from: 'user' });
    }
    if (pClientId) {
      const clientsData = selectClientsData(store);
      const clientObj = clientsData.find((c) => c.id === pClientId) || row?.dimensionValues?.client;
      if (clientObj?.autofillValues?.length > 0) {
        yield HandleAutofillValues({ autofillValues: clientObj.autofillValues, rowId: row.id, from: pClientId });
      }
    }
    if (pProjectId) {
      const projectsData = selectProjectsPerClientId(store, pClientId || 'all');
      const projectObj = projectsData.find((c) => c.id === pProjectId) || row?.dimensionValues?.project;
      if (projectObj?.autofillValues?.length > 0) {
        yield HandleAutofillValues({ autofillValues: projectObj.autofillValues, rowId: row.id, from: pProjectId });
      }
    }
    const taskAutofillValues =
      (taskObj?.autofillValues?.length > 0 && taskObj?.autofillValues) || row?.dimensionValues?.task?.autofillValues;
    if (pTaskId && taskAutofillValues?.length > 0) {
      yield HandleAutofillValues({ autofillValues: taskAutofillValues, rowId: row.id, from: pTaskId });
    }
    if (pItemId) {
      const itemsData = selectItemsData(store);
      const itemObj = itemsData.find((c) => c.id === pItemId);
      if (itemObj?.autofillValues?.length > 0) {
        yield HandleAutofillValues({ autofillValues: itemObj.autofillValues, rowId: row.id, from: pItemId });
      }
    }
    if (pDepartmentObj && pDepartment && pDepartmentObj.autofillValues?.length > 0) {
      yield HandleAutofillValues({ autofillValues: pDepartmentObj.autofillValues, rowId: row.id, from: pDepartment });
    }
    if (pLocationObj && pLocation && pLocationObj.autofillValues?.length > 0) {
      yield HandleAutofillValues({ autofillValues: pLocationObj.autofillValues, rowId: row.id, from: pLocation });
    }
  } catch (error) {
    reportError(error, {
      type: 'AUTOFILL_ERROR',
      payload: {
        row,
        store,
      },
    });
  }
}

export { GenerateRowAutofill, HandleAutofillValues };
